import { isTouchPress } from '../utils/events.ts';
import { suppressNextClick } from './popups.ts';

// How far past a control's edge a touch can lift and still press it, as on
// iOS.
const SLOP = 70;

/**
 * Whether a touch that began at a point is still close enough to a control
 * to press it.
 *
 * The area also reaches to where the touch began, as a link can be stretched
 * over a card by a pseudo-element outside its own box.
 */
export function withinPress(rect: DOMRect, startX: number, startY: number, event: PointerEvent) {
    return (
        event.clientX >= Math.min(rect.left, startX) - SLOP &&
        event.clientX <= Math.max(rect.right, startX) + SLOP &&
        event.clientY >= Math.min(rect.top, startY) - SLOP &&
        event.clientY <= Math.max(rect.bottom, startY) + SLOP
    );
}

/**
 * Let a touch that slides well off a control cancel its press, as on iOS.
 *
 * Browsers click whatever a touch began on, however far it moved before it
 * lifted, unless the page scrolled.
 */
export function enableTapCancelling() {
    document.addEventListener(
        'pointerdown',
        (event) => {
            if (!isTouchPress(event)) return;

            const control = (event.target as Element).closest?.(
                'button, a[href], label, [role=button], [role^=menuitem]',
            );
            if (!control) return;

            const { pointerId, clientX: startX, clientY: startY } = event;
            const listening = new AbortController();
            const { signal } = listening;

            addEventListener(
                'pointerup',
                (event) => {
                    if (event.pointerId !== pointerId) return;
                    listening.abort();

                    if (!withinPress(control.getBoundingClientRect(), startX, startY, event)) {
                        suppressNextClick();
                    }
                },
                { signal },
            );

            addEventListener(
                'pointercancel',
                (event) => {
                    if (event.pointerId === pointerId) listening.abort();
                },
                { signal },
            );
        },
        { passive: true },
    );
}
